import { Button, Input } from '@bbpos/ui';
import {
  BLOCK_LABELS,
  emptyBlock,
  type SitePageBlock,
  type SitePageBlockType,
} from '@/types/site-content';

/**
 * The block editor.
 *
 * Four shapes and no rich text, which is a deliberate limit rather than an
 * unfinished one: the storefront renders these as React elements, so escaping
 * is structural and there is nothing to sanitise. A WYSIWYG here would mean
 * HTML in the database and `dangerouslySetInnerHTML` on a hostname inside the
 * registrable domain every tenant shares. The honest cost is that a shopkeeper
 * cannot bold a word or link their courier.
 *
 * Order matters and is edited with buttons, for the same reasons as the product
 * gallery: no drag dependency, works with a keyboard, works on a tablet.
 */

const TEXTAREA =
  'w-full px-3 py-2 text-sm rounded-lg bg-white/60 dark:bg-neutral-800/60 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y shadow-sm transition-all duration-200';

export function SitePageBlockEditor({
  blocks,
  onChange,
  max,
}: {
  blocks: SitePageBlock[];
  onChange: (blocks: SitePageBlock[]) => void;
  max: number;
}) {
  const replace = (index: number, block: SitePageBlock) =>
    onChange(blocks.map((b, i) => (i === index ? block : b)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div>
      <ul className="space-y-3">
        {blocks.map((block, index) => (
          <li
            key={index}
            className="rounded-xl border border-neutral-200 p-3 transition-all duration-200 hover:shadow-md dark:border-neutral-700"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {BLOCK_LABELS[block.type]}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label="Move up"
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={index === blocks.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label="Move down"
                >
                  ↓
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => onChange(blocks.filter((_, i) => i !== index))}
                  aria-label="Remove this block"
                >
                  ✕
                </Button>
              </div>
            </div>

            <BlockFields block={block} onChange={(b) => replace(index, b)} />
          </li>
        ))}
      </ul>

      {blocks.length >= max ? (
        <p className="mt-3 text-sm text-warning-700 dark:text-warning-400">
          {max} blocks is the limit for one page.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {(Object.keys(BLOCK_LABELS) as SitePageBlockType[]).map((type) => (
            <Button
              key={type}
              size="sm"
              variant="secondary"
              onClick={() => onChange([...blocks, emptyBlock(type)])}
            >
              + {BLOCK_LABELS[type]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockFields({
  block,
  onChange,
}: {
  block: SitePageBlock;
  onChange: (block: SitePageBlock) => void;
}) {
  switch (block.type) {
    case 'heading':
      return (
        <Input
          value={block.text}
          placeholder="A short heading"
          onChange={(text) => onChange({ ...block, text })}
        />
      );

    case 'paragraph':
      return (
        <textarea
          value={block.text}
          rows={3}
          placeholder="Write in your own words."
          className={TEXTAREA}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      );

    case 'list':
      return (
        <div className="space-y-2">
          {block.items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={item}
                placeholder="One point"
                onChange={(value) =>
                  onChange({
                    ...block,
                    items: block.items.map((it, j) => (j === i ? value : it)),
                  })
                }
              />
              <Button
                size="sm"
                variant="secondary"
                aria-label="Remove this point"
                onClick={() =>
                  onChange({ ...block, items: block.items.filter((_, j) => j !== i) })
                }
              >
                ✕
              </Button>
            </div>
          ))}
          {block.items.length < 20 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onChange({ ...block, items: [...block.items, ''] })}
            >
              + Add a point
            </Button>
          )}
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-2">
          <Input
            value={block.question}
            placeholder="The question, as a customer would ask it"
            onChange={(question) => onChange({ ...block, question })}
          />
          <textarea
            value={block.answer}
            rows={2}
            placeholder="Your answer"
            className={TEXTAREA}
            onChange={(e) => onChange({ ...block, answer: e.target.value })}
          />
        </div>
      );

    default:
      return null;
  }
}
